import React, { useEffect, useCallback, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import * as Actions from "actions";

const STATUS_TABS = [
    { value: "active", label: "Active" },
    { value: "waiting", label: "Waiting" },
    { value: "completed", label: "Completed" },
    { value: "failed", label: "Failed" }
];

function formatWhen(value) {
    if (value == null || value === "") {
        return "—";
    }
    const ts = typeof value === "number" ? value : Date.parse(value);
    if (!Number.isFinite(ts)) {
        return "—";
    }
    return new Date(ts).toLocaleString();
}

function statusChipColor(status) {
    switch (status) {
        case "active":
            return "primary";
        case "waiting":
            return "warning";
        case "completed":
            return "success";
        case "failed":
            return "error";
        default:
            return "default";
    }
}

const AdminMediaProcessingJobsMain = (props) => {
    const {
        jobs,
        count,
        queueStatus,
        mediaTypeFilter,
        isLoading,
        serviceError,
        adminGetMediaProcessingJobs
    } = props;

    const [tabStatus, setTabStatus] = useState("active");
    const [mediaType, setMediaType] = useState("all");

    const load = useCallback(() => {
        adminGetMediaProcessingJobs(tabStatus, mediaType, 100);
    }, [adminGetMediaProcessingJobs, tabStatus, mediaType]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        const poll = tabStatus === "active" || tabStatus === "waiting";
        if (!poll) {
            return undefined;
        }
        const id = setInterval(load, 8000);
        return () => clearInterval(id);
    }, [load, tabStatus]);

    const handleTabChange = (event, value) => {
        if (value != null) {
            setTabStatus(value);
        }
    };

    const handleMediaTypeChange = (event, value) => {
        if (value != null) {
            setMediaType(value);
        }
    };

    let errText = "";
    if (typeof serviceError === "string") {
        errText = serviceError;
    } else if (serviceError && serviceError.message) {
        errText = serviceError.message;
    } else if (serviceError) {
        errText = JSON.stringify(serviceError);
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
                <Box>
                    <Typography variant="h4" gutterBottom>
                        Media processing jobs
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Async image and video jobs from the unified media queue (Bull / Redis), by queue state.
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={isLoading ? <CircularProgress size={18} /> : <RefreshIcon />}
                    onClick={load}
                    disabled={isLoading}
                >
                    Refresh
                </Button>
            </Box>

            <Paper sx={{ mb: 2, px: 1 }}>
                <Tabs
                    value={tabStatus}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {STATUS_TABS.map((t) => (
                        <Tab key={t.value} value={t.value} label={t.label} />
                    ))}
                </Tabs>
            </Paper>

            <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Media type
                </Typography>
                <ToggleButtonGroup
                    exclusive
                    value={mediaType}
                    onChange={handleMediaTypeChange}
                    size="small"
                >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="image">Images</ToggleButton>
                    <ToggleButton value="video">Videos</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {errText ? (
                <Typography color="error" sx={{ mb: 2 }}>{errText}</Typography>
            ) : null}

            <Paper>
                <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="subtitle2">
                        Showing {count} job{count === 1 ? "" : "s"}
                    </Typography>
                    <Chip size="small" label={`State: ${queueStatus}`} />
                    {mediaTypeFilter && mediaTypeFilter !== "all" ? (
                        <Chip size="small" label={`Filter: ${mediaTypeFilter}`} />
                    ) : null}
                </Box>
                <TableContainer sx={{ maxHeight: "70vh" }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Job ID</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Progress</TableCell>
                                <TableCell>Product</TableCell>
                                <TableCell>User</TableCell>
                                <TableCell align="right">Images</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Started</TableCell>
                                <TableCell>Finished</TableCell>
                                <TableCell align="right">Attempts</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {jobs.length === 0 && !isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={11}>
                                        <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                                            No jobs in this view.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : null}
                            {jobs.map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell>{row.id}</TableCell>
                                    <TableCell>{row.media_type || "—"}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={row.status}
                                            color={statusChipColor(row.status)}
                                            variant={row.status === "completed" ? "outlined" : "filled"}
                                        />
                                    </TableCell>
                                    <TableCell align="right">{row.progress != null ? `${row.progress}%` : "—"}</TableCell>
                                    <TableCell sx={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {row.product_id || "—"}
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {row.user_id || "—"}
                                    </TableCell>
                                    <TableCell align="right">
                                        {row.image_count != null ? row.image_count : "—"}
                                    </TableCell>
                                    <TableCell>{formatWhen(row.created_at)}</TableCell>
                                    <TableCell>{formatWhen(row.processed_on)}</TableCell>
                                    <TableCell>{formatWhen(row.finished_on)}</TableCell>
                                    <TableCell align="right">{row.attempts_made != null ? row.attempts_made : "—"}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {isLoading && jobs.length > 0 ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                        <CircularProgress size={22} />
                    </Box>
                ) : null}
            </Paper>
        </Container>
    );
};

const mapState = (state) => {
    const slice = state.adminMediaProcessingJobs || {};
    return {
        jobs: slice.jobs || [],
        count: slice.count != null ? slice.count : 0,
        queueStatus: slice.queueStatus || "completed",
        mediaTypeFilter: slice.mediaTypeFilter || "all",
        isLoading: slice.isLoading || false,
        serviceError: slice.serviceError
    };
};

const mapDispatch = (dispatch) => bindActionCreators({
    adminGetMediaProcessingJobs: Actions.adminGetMediaProcessingJobs
}, dispatch);

export default connect(mapState, mapDispatch)(AdminMediaProcessingJobsMain);
